import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
    GET_PROJECT_MILESTONES_BY_PROJECT_TYPE_ID,
    RelatedFormFieldQuery,
} from '@api/queries/projects';
import useUser from '@hooks/useUser';
import { mapQueryResultToOptions } from '@pages/projects/_utils/utils/mapQueryResultToStringOption';
import { type StringOption } from '@models/Option';
import { type User } from '@gql/graphql';
import { GET_USERS_FOR_SELECT } from '@api/queries/users';
import { flatMapEdges } from '@utils/flatMapEdges';
import { type ProjectFormAttributes } from '../../ProjectForm';

export function useProjectFormDetailsState() {
    const {
        watch,
        setValue,
        getValues,
        formState: { isDirty },
    } = useFormContext<ProjectFormAttributes>();

    const { pid } = useParams();

    const userIDs = watch('userIDs', []);
    const typeID = watch('typeID', '');

    const { data: usersData, loading: usersLoading } = useQuery(
        GET_USERS_FOR_SELECT,
        {
            variables: {
                where: { active: true },
            },
        },
    );

    const { data: projectMilestonesData, loading: milestonesLoading } =
        useQuery(GET_PROJECT_MILESTONES_BY_PROJECT_TYPE_ID, {
            skip: !typeID,
            variables: { projectTypeId: typeID },
        });

    const relatedQuery = useQuery(RelatedFormFieldQuery);
    const currentUser = useUser();

    const users: User[] = useMemo(
        () => flatMapEdges(usersData?.users) as User[],
        [usersData],
    );

    const projectMembersOptions: StringOption[] = useMemo(
        () =>
            users
                .filter((user) => userIDs.includes(user.id))
                .map((user) => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                })),
        [users, userIDs],
    );

    const { organizations, projectStatuses, projectTypes } =
        relatedQuery.data ?? {};

    const projectTypeOptions = mapQueryResultToOptions(projectTypes);
    const statusOptions = mapQueryResultToOptions(projectStatuses);

    const organizationOptions = useMemo(
        () => mapQueryResultToOptions(organizations),
        [organizations],
    );

    const projectMilestonesOptions = useMemo(
        () =>
            projectMilestonesData
                ? mapQueryResultToOptions(projectMilestonesData.projectMilestones)
                : [],
        [projectMilestonesData],
    );

    const validMilestoneIds = useMemo(
        () => projectMilestonesOptions.map((option) => option.value),
        [projectMilestonesOptions],
    );

    const validateMilestone = useCallback(
        (value: string | undefined) => {
            if (!value) {
                return 'Please select a project milestone';
            }
            if (
                projectMilestonesOptions.length > 0 &&
                !validMilestoneIds.includes(value)
            ) {
                return 'Selected milestone is not valid for this project type';
            }

            return true;
        },
        [projectMilestonesOptions, validMilestoneIds],
    );

    useEffect(() => {
        const currentMilestone = getValues('milestoneID');
        if (
            projectMilestonesOptions.length > 0 &&
            currentMilestone &&
            !validMilestoneIds.includes(currentMilestone)
        ) {
            setValue('milestoneID', '', { shouldValidate: true });
        }
    }, [projectMilestonesOptions, validMilestoneIds, getValues, setValue]);

    useEffect(() => {
        if (!currentUser || isDirty || !organizationOptions.length) {
            return;
        }
        const userOrg = organizationOptions.find(
            (org) => org.label === currentUser.orgCode,
        );
        if (userOrg) {
            setValue('organizationID', userOrg.value);
        }
    }, [organizationOptions, currentUser, isDirty, setValue]);

    useEffect(() => {
        if (!pid && currentUser) {
            setValue('securityResponsibleID', currentUser.id);
        }
    }, [pid, currentUser, setValue]);

    const [showDeprecated, setShowDeprecated] = useState(false);

    const hasDeprecatedTypes = projectTypeOptions.some((option) =>
        option.label.toLowerCase().startsWith('deprecated -'),
    );

    const relatedLoading = relatedQuery.loading;
    const showStepLoadingOverlay =
        (relatedLoading && !relatedQuery.data) ||
        (usersLoading && users.length === 0);

    const toggleDeprecatedTypes = (): void => {
        setShowDeprecated((previousValue) => !previousValue);
    };

    return {
        pid,
        currentUser,
        usersLoading,
        milestonesLoading,
        relatedLoading,
        showStepLoadingOverlay,
        showDeprecated,
        hasDeprecatedTypes,
        projectTypeOptions,
        statusOptions,
        organizationOptions,
        projectMilestonesOptions,
        projectMembersOptions,
        validateMilestone,
        toggleDeprecatedTypes,
    };
}
